import React, { createContext, useContext, useEffect, useState } from "react";
import api from "../lib/api";
import { safeLocal } from "../lib/safeStorage";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = loading
  const [ready, setReady] = useState(false);

  const loadMe = async () => {
    const token = safeLocal.get("ss_token");
    if (!token) {
      setUser(false);
      setReady(true);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      // Only drop the token on an explicit 401 (bad/expired session). A
      // transient network/5xx error must not destroy a valid session, leave
      // the token in place so a later load can retry.
      if (e?.response?.status === 401) safeLocal.remove("ss_token");
      setUser(false);
    } finally {
      setReady(true);
    }
  };

  useEffect(() => {
    loadMe();
  }, []);

  // Global session-expiry handler (fired by the axios 401 interceptor):
  // drop the stale token and flip to logged-out so the router sends the
  // member to the landing/sign-in page.
  useEffect(() => {
    const onExpired = () => { safeLocal.remove("ss_token"); setUser(false); };
    window.addEventListener("ss:session-expired", onExpired);
    return () => window.removeEventListener("ss:session-expired", onExpired);
  }, []);

  const setSession = (token, userData) => {
    safeLocal.set("ss_token", token);
    setUser(userData);
  };

  const logout = () => {
    safeLocal.remove("ss_token");
    setUser(false);
  };

  const refresh = loadMe;

  // One-time coach tips: remembered per-account (user.dismissed_tips).
  // One-time coach tips, remembered per-account. Dismissal is keyed per-revision
  // (`id@rev`) so editing/bumping one tip re-shows only that tip.
  const dismissTip = async (id, rev = 1) => {
    const key = `${id}@${rev}`;
    setUser((u) => (u ? { ...u, dismissed_tips: [...(u.dismissed_tips || []), key] } : u));
    try { await api.post("/tips/dismiss", { id, rev }); } catch { /* non-blocking */ }
  };

  // Pass tip ids to re-show only those (e.g. one screen); omit to reset all.
  const resetTips = async (screen, ids) => {
    setUser((u) => (u ? { ...u, dismissed_tips: ids ? (u.dismissed_tips || []).filter((k) => !ids.includes(k.split("@")[0])) : [] } : u));
    try { await api.post(screen ? `/tips/reset?screen=${encodeURIComponent(screen)}` : "/tips/reset"); } catch { /* non-blocking */ }
  };

  return (
    <AuthContext.Provider value={{ user, ready, setSession, logout, refresh, setUser, dismissTip, resetTips }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
